<template lang="html">
  <section :id="id">
    <base-modal
      ref="baseModal"
      :maxHeight="500"
      :maxWidth="700"
      :noPadding="true"
      @on-base-modal-close="cancelModal"
    >
      <!-- header  -->
      <template v-slot:header>
        <div class="title">{{ widgetTranslatedName }}</div>
      </template>

      <element-description
        v-if="isManifestReady"
        :label="descriptionText"
        :label-class="'cde-desc-label'"
        :outer-container-class="'description-container'"
        :elem-description="desc"
        :focus="true"
        :value-container-class="'cde-desc-val-box'"
        @on-element-description-changed="onDeDescriptionChanged"
      ></element-description>

      <!-- add content -->
      <custom-display-element-manifest-config
        id="manifest-config-members"
        :widget-data="widgetData"
        @on-manifest-config-ready="onManifestConfigReady"
      />

      <!-- footer  -->
      <template v-slot:footer>
        <div class="m-group-btns">
          <button class="m-button neutral" @click.stop="cancelModal">
            {{ getMessageText(messages.CANCEL) }}
          </button>
          <button
            class="m-button brand"
            @click.stop="createBtn"
            :disabled="isSubmitDisabled"
          >
            {{ getMessageText(messages.OK) }}
          </button>
        </div>
      </template>
    </base-modal>
  </section>
</template>

<script lang="js">
  import { MUTATION_NAME } from "@/static/global-variables";
  import distributionChannelUtil from "@/utils/distribution-channel-util";
  import dragAndDrop from "@/views/drag-drop-pages/components/utils/dragAndDrop";
  import BaseModal from "@/views/drag-drop-pages/components/modal/BaseModal";
  import CustomDisplayElementManifestConfig from "@/components/custom-display-elements/page-elements/CustomDisplayElementManifestConfig";
  import ElementDescription from "@/components/ElementDescription";

  export default  {
    name: "add-edit-custom-display-element-modal",
    components: {
      BaseModal,
      CustomDisplayElementManifestConfig,
      ElementDescription
    },
    props: {
      id: String,
      // For editing an already existing element on canvas
      editData: Object,
      // When first dragged on to the canvas
      widget: {
        type: Object,
        default: function() {
          return {};
        }
      },
      version: String
    },
    data () {
      return {
        isManifestReady: false,
        widgetDescription: "",
        widgetData: {
          widget: {},
          uuid: null,
          version: null,
          properties: {},
          customMembers: {
            members: [],
            properties: {}
          },
          customMemberFields: {}
        }
      }
    },
    computed: {
      messages() {
        return this.$store.state.messages;
      },
      isSubmitDisabled() {
        return !distributionChannelUtil.isValidCustomDisplayElementConfig(this.$store.getters[MUTATION_NAME.ADD_EDIT_WIDGET_DATA]);
      },
      descriptionText() {
        return this.getMessageText(this.messages.DESCRIPTION);
      },
      desc() {
        return this.widgetData ? this.widgetData.description : "";
      },
      userLanguage() {
        const user = this.$store.getters.user;
        return user ? user.language : "en_US";
      },
      allWidgetTranslations() {
        return this.$store.getters[MUTATION_NAME.ALL_WIDGET_TRANSLATIONS] || {};
      },
      translationsOfWidget() {
        let widgetId;

        if (this.widget && this.widget.widgetId) {
          widgetId = this.widget.widgetId
        } else {
          widgetId = this.widgetData.widget.widgetId;
        }
        return this.allWidgetTranslations[widgetId] || [];
      },
      widgetTranslatedName() {
        let originalName;
        if (this.widget && this.widget.name) {
          originalName = this.widget.name;
        } else {
          originalName = this.widgetData.widget.name;
        }
        return distributionChannelUtil.findTranslatedLabel(this.translationsOfWidget, this.userLanguage) || originalName;
      },
    },
    watch: {
      widget: function() {
        // Setup data only the first time  when this modal is added to canvas,
        // For edit mode, this will never be triggered
        this.widgetData.widget = JSON.parse(JSON.stringify(this.widget));
        this.widgetData.uuid = this.widget.widgetId;
        this.widgetData.version = this.version;
        this.widgetData.description = "";
        this.$store.commit(MUTATION_NAME.ADD_EDIT_WIDGET_DATA, this.widgetData);
      }
    },
    mounted () {
      if(this.editData) {
        this.setupEditModalData();
      }
    },
    methods: {
      setupEditModalData() {
        // This will be called when editing an existing widget
        this.widgetData = JSON.parse(JSON.stringify(this.editData.props));
        this.widgetData.customMembers = {
            members: [],
            properties: {}
          };
        this.widgetData.customMemberFields = {};
        this.widgetDescription = this.widgetData.description;
        this.$store.commit(MUTATION_NAME.ADD_EDIT_WIDGET_DATA, this.widgetData);
      },
      cancelModal() {
        this.$emit("on-add-modal-close", {id: this.id});
        if(this.widgetData.newVersion) {
          // trigger canvas update to reset since no changes are done
          dragAndDrop.triggerCanvasUpdate(true);
        }
      },
      createBtn() {
        const obj = {
          modal: {
            id: this.id,
            create: "CustomDisplayElementConfig",
            editElementId: this.editData? this.editData.id: null
          },
          // specific to custom display element
          // This object will be passed on to the configutation component
          props: this.getConfigData()
        };
        this.$emit("on-add-modal-done", obj);
      },
      trimNullOptionalProperties(properties) {
        Object.keys(properties).forEach(key => {
          if ((!properties[key].expression && !distributionChannelUtil.isMemberFieldTypeArray(properties[key].type))
            || (!properties[key].dataSource && distributionChannelUtil.isMemberFieldTypeArray(properties[key].type))) {
            delete properties[key];
          }
        })
      },
      getConfigData() {
        const allProperties = distributionChannelUtil.getAllProperties(this.widgetData);
        // Remove optional properties before sending to Elm if they are null
        if (allProperties) {
          this.trimNullOptionalProperties(allProperties);
        }
        return {
          uuid: this.widgetData.uuid,
          version: this.widgetData.version,
          description: this.widgetDescription,
          properties: allProperties
        }
      },
      onDeDescriptionChanged(input) {
        this.widgetDescription = input;
      },
      onManifestConfigReady(val) {
        this.isManifestReady = val;
      }
    }
  }
</script>

<style scoped lang="scss">
  .title {
    text-align: initial;
  }

  .description-container {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
  }
</style>
